import $ from 'jquery';
import moment from 'moment';

var KEY_CODE_INFO = {
        'backspace': 8,
        'del': 46,
        'end': 35,
        'home': 36,
        'left': 37,
        'right': 39,
        'shift': 16,
        'ctrl': 17,
        'tab': 9,
        'esc': 27,
        'enter': 13,
        'a': 65, // for ctrl-a
        'c': 67, // for ctrl-c
        'x': 88, // for ctrl-x
        // end: 35, home: 36, left: 37, right: 39
        'navigatorKeys': [35, 36, 37, 39],
        // backspace: 8, del: 46
        'deleteKeys': [8, 46],
        // a: 65, c: 67, x: 88 (with ctrl)
        'selectorKeys': [65, 67, 88],
        // shift: 16, ctrl: 17, alt: 18
        'modifierKeys': [16, 17, 18],
        // esc: 27
        'exitKeys': [27],
        // numbers (0..9): 48..57
        'numberKeys': [48, 49, 50, 51, 52, 53, 54, 55, 56, 57],
        // numbers (0..9) on keypad: 96..105
        'keypadNumberKeys': [96, 97, 98, 99, 100, 101, 102, 103, 104, 105],
        // tab: 9, enter: 13
        'setterKeys': [9, 13]
    };

/**
 * @param keyEvent is a keyboard event which encapsulates further information such as
 *        the numerical code of the pressed key and status of modifier keys when the event occurred
 *
 * @return boolean value that is "true" if CTRL with a selector key is pressed
 */
export function isSelectorKey(keyEvent) {
    var selectAllKey = false;
    if (keyEvent.ctrlKey) {
        selectAllKey = $.inArray(keyEvent.which, KEY_CODE_INFO.selectorKeys) !== -1;
    }
    return selectAllKey;
}

/**
 * @param keyEvent is a keyboard event which encapsulates further information such as
 *        the numerical code of the pressed key and status of modifier keys when the event occurred
 *
 * @return boolean value that is "true" if a navigator key is pressed
 */
export function isNavigatorKey(keyEvent) {
    return $.inArray(keyEvent.which, KEY_CODE_INFO.navigatorKeys) !== -1;
}

/**
 * @param keyEvent is a keyboard event which encapsulates further information such as
 *        the numerical code of the pressed key and status of modifier keys when the event occurred
 *
 * @return boolean value that is "true" if a number key without any modifier keys is pressed
 */
export function isOnlyNumberKey(keyEvent) {
    var numberKey = $.inArray(keyEvent.which, KEY_CODE_INFO.numberKeys) !== -1,
        keypadNumberKey = $.inArray(keyEvent.which, KEY_CODE_INFO.keypadNumberKeys) !== -1,
        nonModified = !keyEvent.altKey && !keyEvent.ctrlKey && !keyEvent.shiftKey;
    return nonModified && (numberKey || keypadNumberKey);
}

/**
 * @param keyEvent is a keyboard event which encapsulates further information such as
 *        the numerical code of the pressed key and status of modifier keys when the event occurred
 *
 * @return boolean value that is "true" if an delete key is pressed
 */
export function isDeleteKey(keyEvent) {
    return $.inArray(keyEvent.which, KEY_CODE_INFO.deleteKeys) !== -1;
}

/**
 * @param keyEvent is a keyboard event which encapsulates further information such as
 *        the numerical code of the pressed key and status of modifier keys when the event occurred
 *
 * @return boolean value that is "true" if an exit key is pressed
 */
export function isExitKey(keyEvent) {
    return $.inArray(keyEvent.which, KEY_CODE_INFO.exitKeys) !== -1;
}

/**
 * @param keyEvent is a keyboard event which encapsulates further information such as
 *        the numerical code of the pressed key and status of modifier keys when the event occurred
 *
 * @return boolean value that is "true" if a setter key is pressed
 */
export function isSetterKey(keyEvent) {
    return $.inArray(keyEvent.which, KEY_CODE_INFO.setterKeys) !== -1;
}

/**
 * @param keyEvent is a keyboard event which encapsulates further information such as
 *        the numerical code of the pressed key and status of modifier keys when the event occurred
 *
 * @return boolean value that is "true" if a modifier key is pressed (e.g. 'ALT', 'SHIFT' or 'CRTL')
 */
export function isModifierKey(keyEvent) {
    return $.inArray(keyEvent.which, KEY_CODE_INFO.modifierKeys) !== -1;
}

/**
 * @param $input input element
 *
 * @return boolean value that is "true" if there is a selected text in the input element
 */
export function isTextSelected($input) {
    var selected = false,
        selectionStart = $input.selectionStart,
        selectionEnd = $input.selectionEnd;
    if (selectionEnd) {
        // on all browsers, except IE before version 9
        selected = selectionStart < selectionEnd;
    } else if (document.selection) {
        // on IE before version 9
        selected = document.selection.type === 'Text';
    }
    return selected;
}

/**
 * @param westernWeekday the day position in the western calendar week from Sunday to Saturday (0 to 6)
 *
 * @return number the day position in the ISO calendar week from Monday to Sunday (1 to 7)
 */
export function convertWesternToIsoWeekday(westernWeekday) {
    var normalizedWesternWeekday = westernWeekday % 7,
        isoWeekday = normalizedWesternWeekday === 0 ? 7 : normalizedWesternWeekday;
    return isoWeekday;
}

/**
 * @param isoWeekday the day position in the western calendar week from Monday to Sunday (1 to 7)
 *
 * @return number the day position in the western calendar week from Sunday to Saturday (0 to 6)
 */
export function convertIsoToWesternWeekday(isoWeekday) {
    var westernWeekday = isoWeekday % 7;
    return westernWeekday;
}

/**
 * @param year max. 4-digit number or string representation of the year
 *
 * @return moment date object which represents the first day of the year
 */
export function getFirstDayOfYear(year) {
    var day = moment(year + '-01-01', 'YYYY-MM-DD');
    return day;
}

/**
 * @param date moment date object
 *
 * @return moment object which represents the first day of the month
 */
export function getFirstDayOfMonth(date) {
    var day = moment(date);
    day.startOf('month');
    return day;
}

/**
 * @param date moment date object
 * @param isoWeekStart the first day of the week from Sunday to Saturday (0 to 6)
 *
 * @return number between 0 and 6 that represents the day position after the weekStartDay
 */
export function getDayOfWeek(date, isoWeekStart) {
    var isoWeekday = date.isoWeekday(),
        // isoWeekdayOffset: weekdays offset is based on Monday (0..6)
        isoWeekdayOffset = isoWeekStart - 1,
        day = isoWeekday - isoWeekdayOffset;
    if (day < 1) {
        day += 7;
    }
    return day;
}

/**
 * @param date moment date object
 * @param displayedYearOffset the first day of the year from Monday to Sunday (0 to 6)
 *
 * @return moment object which represents the week of the year
 */
export function getWeekOfYear(date, displayedYearOffset) {
    var day = date.dayOfYear(),
        extendedYear = day + displayedYearOffset,
        week = Math.ceil(extendedYear / 7);
    return week;
}

/**
 * @param date moment date object
 * @param displayedYearOffset the first day of the year from Monday to Sunday (0 to 6)
 *
 * @return moment object which represents the week of the month
 */
export function getWeekOfMonth(date, displayedYearOffset) {
    var weekOfYear = getWeekOfYear(date, displayedYearOffset),
        firstDay = getFirstDayOfMonth(date),
        firstDayWeekOfYear = getWeekOfYear(firstDay, displayedYearOffset),
        week = weekOfYear - firstDayWeekOfYear + 1;
    return week;
}

/**
 * @param str the date string
 * @param dateFormat the date format string
 * @param defaultDate default date (moment object) when the given string format does not match
 *        the date format string (optional parameter: today by default)
 *
 * @return date (moment object)
 */
export function parseDate(dateString, dateFormat, defaultDate) {
    var date = moment(dateString || '', dateFormat),
        isValid = date.isValid();
    return isValid ? date : defaultDate || moment();
}

/**
 * This function is to be used to find base element with given class or its parent with given class.
 *
 * @param $elem The jQuery elem.
 * @param cssClass The main CSS class associated with the widget.
 */
export function findBaseElemByClass($elem, targetClass) {
    if ($elem.hasClass(targetClass)) {
        return $elem;
    }
    return $elem.length ? findBaseElemByClass($elem.parent(), targetClass) : undefined;
}

/**
 * This function converts array to array of objects which contain the item and the index.
 *
 * @param array array of items
 * @param fieldName the item field name and the prefix for index field name in the object of the returned array item
 *
 *  @retrun array of objects which contain item object in the fieldName
 *          and index number in the fieldName + 'Index' fields
 */
export function createIndexedArray(array, fieldName) {
    var indexedArray = [],
        fieldIndex = fieldName + 'Index',
        item,
        index = 0;
    $(array).each(function () {
        item = {};
        item[fieldName] = this;
        item[fieldIndex] = index;
        indexedArray[index++] = item;
    });
    return indexedArray;
}

/**
 * This function sets the buttom element disabled status.
 *
 * @param $button button element
 * @param disabled if "true" the button will be disabled
 */
export function setButtonDisabled($button, disabled) {
    $button.prop('disabled', disabled);
    if (disabled) {
        $button.addClass('uwr-button-disabled');
    } else {
        $button.removeClass('uwr-button-disabled');
    }
}

/**
 * This function checks that search string exists in the text and it is
 * unique or not.
 *
 * @param text the string text
 * @param search the search string
 *
 * @return boolean value that is "true" if search text exists and is unique in text
 */
export function isUniqueSubstr(text, search) {
    var index = text ? text.indexOf(search) : -1,
        lastIndex = text ? text.lastIndexOf(search) : -1,
        match = index !== -1,
        unique = index === lastIndex;
    return match && unique;
}



//////////////////
// WEBPACK FOOTER
// ./widgets/DatePicker/js/datePickerUtil.js
// module id = 86
// module chunks = main